import React, { useMemo, useState } from "react";
import styled from "styled-components";
import type { TimeseriesDayPoint } from "@/api/campaign";
import { formatDayMonth, formatWeekdayDayMonth } from "./chartTheme";
import { DailyLineChart } from "./DailyLineChart";
import { DailyWeekChart } from "./DailyWeekChart";
import { dailyPeriod, todayIn } from "./dailyPeriod";
import { PeriodNav } from "./PeriodNav";
import { TimeseriesChartFrame, type ChartPlatform } from "./TimeseriesChartFrame";
import { TAP_TARGET } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";

/*
 * Phone view of TimeseriesChart, in one card with a Monthly / Weekly toggle. Both show daily values on
 * the same chart (DailyLineChart); only the window differs: Monthly is one calendar month, Weekly one
 * week (DailyWeekChart). Both step with arrows, and a period still in progress ends at today with a
 * little space after it. Tapping the chart shows the day's values.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** "2026-09" for a day. */
const monthOf = (date: string) => date.slice(0, 7);
const monthTitle = (month: string) =>
	new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-AU", { month: "long", year: "numeric", timeZone: "UTC" });
/** Every day of a calendar month, "2026-09-01" to "2026-09-30". */
const daysOfMonth = (month: string) => {
	const first = Date.parse(`${month}-01T00:00:00Z`);
	const days: string[] = [];
	for (let time = first; monthOf(new Date(time).toISOString()) === month; time += DAY_MS) {
		days.push(new Date(time).toISOString().slice(0, 10));
	}
	return days;
};

type ChartView = "monthly" | "weekly";

const MonthNav = styled.div`
	margin-top: 12px;
`;

const VIEWS: Array<{ key: ChartView; label: string }> = [
	{ key: "monthly", label: "Monthly" },
	{ key: "weekly", label: "Weekly" },
];

/** Branded pill toggle: the chosen view is a filled purple pill. */
const ViewToggle = styled.div`
	display: flex;
	gap: 4px;
	margin-top: 12px;
	padding: 3px;
	border-radius: 999px;
	background: #f3f5ff;
`;

const ViewOption = styled.button<{ $active: boolean }>`
	flex: 1 1 0;
	min-height: ${TAP_TARGET}px;
	border: 0;
	border-radius: 999px;
	background: ${({ $active }) => ($active ? "#3a0ca3" : "transparent")};
	color: ${({ $active }) => ($active ? "#ffffff" : "#454545")};
	font:
		600 14px "Poppins",
		sans-serif;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: 2px;
	}

	${pressedTint}
`;

interface WeeklyTimeseriesChartProps {
	/** Daily points, oldest first, leading empty days already dropped. */
	series: TimeseriesDayPoint[];
	platform: ChartPlatform;
	timezone: string;
	loading: boolean;
}

export const WeeklyTimeseriesChart: React.FC<WeeklyTimeseriesChartProps> = ({
	series,
	platform,
	timezone,
	loading,
}) => {
	const [view, setView] = useState<ChartView>("monthly");

	// The picked week (its Monday) belongs to the series it was picked from, so another platform or
	// period opens on its latest week without an effect to reset it.
	const [pick, setPick] = useState<{ series: TimeseriesDayPoint[]; week: string } | null>(null);
	const selectedWeek = pick?.series === series ? pick.week : null;
	const selectWeek = (week: string) => setPick({ series, week });

	// The month on show follows the picked week until a month arrow is used; it opens on the latest month.
	const months = useMemo(() => Array.from(new Set(series.map((point) => monthOf(point.date)))).sort(), [series]);
	const [monthPick, setMonthPick] = useState<{ series: TimeseriesDayPoint[]; month: string } | null>(null);
	const month =
		(monthPick?.series === series ? monthPick.month : null) ??
		(selectedWeek ? monthOf(selectedWeek) : months[months.length - 1]);
	const monthIndex = months.indexOf(month);

	const today = todayIn(timezone);
	const byDay = useMemo(() => new Map(series.map((point) => [point.date, point])), [series]);
	const monthPeriod = dailyPeriod(
		month ? daysOfMonth(month) : [],
		byDay,
		today,
		formatDayMonth,
		formatWeekdayDayMonth,
	);

	const hasData = series.length > 0;
	return (
		<TimeseriesChartFrame
			platform={platform}
			note="Daily values of observed days"
			timezone={timezone}
			loading={loading}
			hasData={hasData}
			toolbar={
				<ViewToggle role="group" aria-label="Chart view">
					{VIEWS.map((option) => (
						<ViewOption
							key={option.key}
							type="button"
							$active={view === option.key}
							aria-pressed={view === option.key}
							onClick={() => setView(option.key)}
						>
							{option.label}
						</ViewOption>
					))}
				</ViewToggle>
			}
		>
			{view === "weekly" ? (
				<DailyWeekChart series={series} weekStart={selectedWeek} onWeekChange={selectWeek} today={today} />
			) : (
				<>
					<MonthNav>
						<PeriodNav
							title={monthTitle(month)}
							unit="month"
							onPrevious={() => setMonthPick({ series, month: months[monthIndex - 1] })}
							onNext={() => setMonthPick({ series, month: months[monthIndex + 1] })}
							hasPrevious={monthIndex > 0}
							hasNext={monthIndex < months.length - 1}
						/>
					</MonthNav>
					<DailyLineChart {...monthPeriod} maxTicks={5} />
				</>
			)}
		</TimeseriesChartFrame>
	);
};
